import { useEffect, useState } from "react";
import { DSCommunitySchema } from "../data/types";
import { DeleteConfirmationModal } from "../molecules/AppModal";

import UserAvatar from "../components/UserAvatar";
import { AddCircle } from "@mui/icons-material";
import {ReactComponent as AddTeamIcon} from "../icons/AddTeamIcon.svg"
import { InviteTeamMemberModal } from "./AppModal";
import { TeamMemberSchema, PermissionCategory, CommunityPermissions, CommunityPermissionFlag } from "../data/types";
import { capitalizeString, normalizeTeamMembers } from "../utils/helpers";
import { TEAM_PERMISSION_LABELS } from "../utils/teamPermissions";
import { useManageTeam } from "../hooks/communityhook";
import { useAuthState } from "../contexts/authcontext";
import moment from "moment-timezone";
import { toast } from "sonner";



/** Identity used to keep View Access open across team query refreshes. */
const getTeamMemberIdentityKey = (member: TeamMemberSchema): string =>
  String(member.email || member.username || "").trim().toLowerCase();

/** Collapse View Access on load; keep a row expanded after the partner opens it. */
const mergeTeamMembersFromQuery = (
  incoming: TeamMemberSchema[],
  current: TeamMemberSchema[]
): TeamMemberSchema[] => {
  const expandedByIdentity = new Map(
    current.map((member) => [getTeamMemberIdentityKey(member), !!member.permissions?.community?.expanded])
  );
  return incoming.map((member) => ({
    ...member,
    permissions: {
      ...member.permissions,
      community: {
        ...member.permissions.community,
        expanded: expandedByIdentity.get(getTeamMemberIdentityKey(member)) ?? false,
      },
    },
  }));
};

type TeamRolesProps = {
  team?: TeamMemberSchema[]
  community: DSCommunitySchema
  currentCommunity: DSCommunitySchema
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void
}


export const TeamRoles: React.FC<TeamRolesProps> = ({community}) => {
  const { authUser } = useAuthState()
  const { team, deleteTeamMemberData, updateTeamMemberData } = useManageTeam()
  const signedInEmail = String(authUser?.email || authUser?.username || "").trim().toLowerCase()

  const getStatusColorClasses = (status: string): string => {
    switch (status) {
      case "invited":
        return "text-blue-600/60";
      case "accepted":
        return "text-green-600/60";
      case "rejected":
        return "text-red-600/60";
      case "expired":
        return "text-orange-600/60";
      case "removed":
        return "text-gray-600/60";
      default:
        return "text-gray-500/60";
    }
  };
  const [showInviteModal, setShowInviteModal] = useState<boolean>(false);
  const [imageError, setImageError] = useState<boolean>(false)
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);
  const [isDeletingTeamMember, setIsDeletingTeamMember] = useState<{isOpen: boolean, member: TeamMemberSchema}>({isOpen: false, member: {} as TeamMemberSchema});

  const handleDeleteTeamMember = async (memberEmail: string) => {
    const email = String(memberEmail || "").trim().toLowerCase();
    const result = await deleteTeamMemberData(email);
    setIsDeletingTeamMember({isOpen: false, member: {} as TeamMemberSchema});
    if (result) {
      toast.success("Team member removed");
      return;
    }
    toast.error("Could not remove team member. Please try again.");
  };

  const [teamMembers, setTeamMembers] = useState<TeamMemberSchema[]>(() =>
    mergeTeamMembersFromQuery(
      team ? normalizeTeamMembers(team as TeamMemberSchema[]) : [],
      []
    )
  );

  useEffect(() => {
    if (!team) {
      return;
    }
    const nextMembers = normalizeTeamMembers(team as TeamMemberSchema[]);
    setTeamMembers((current) => mergeTeamMembersFromQuery(nextMembers, current));
  }, [team]);

  const togglePermission = async (memberKey: string, category: string, permission: string) => {
    const target = teamMembers.find((member) => member.email === memberKey);
    if (!target || category !== "community") {
      return;
    }
    const currentPermissions = (target.permissions.community.permissions || {}) as CommunityPermissions;
    const nextPermissions = {
      ...currentPermissions,
      [permission]: !currentPermissions[permission as CommunityPermissionFlag],
    };
    setTeamMembers((prev) =>
      prev.map((member) =>
        member.email === memberKey
          ? {
              ...member,
              permissions: {
                ...member.permissions,
                community: {
                  ...member.permissions.community,
                  permissions: nextPermissions,
                },
              },
            }
          : member
      )
    );
    const result = await updateTeamMemberData({
      email: memberKey,
      permissions: {
        community: {
          expanded: false,
          permissions: nextPermissions,
        },
      },
    });
    if (!result) {
      toast.error("Could not update permissions. Please try again.");
    }
  };

  const toggleCategory = (memberKey: string, category: string) => {
    setTeamMembers(prev => prev.map(member => {
      if ((member.id && member.id === memberKey) || member.email === memberKey) {
        return {
          ...member,
          permissions: {
            ...member.permissions,
            [category]: {
              ...member.permissions[category as keyof typeof member.permissions],
              expanded: !member.permissions[category as keyof typeof member.permissions].expanded
            }
          }
        };
      }
      return member;
    }));
  };

  const canDeleteMember = (member: TeamMemberSchema) => {
    const memberEmail = String(member.email || member.username || "").trim().toLowerCase();
    return member.role !== "owner" && memberEmail !== signedInEmail;
  };

  return (
    <>
      <div className="flex flex-row bg-white h-[75px] w-full border-t-[#E6E6E6] border-t-[1px]">
        <p className="mx-[24px] my-auto font-open-sans text-[16px] text-[#4D4D4D]/70">
          View and edit your team roles and permissions.
        </p>
      </div>
      
      <div className="flex flex-col w-11/12 mx-auto">
        <div className="flex flex-col bg-white my-3 h-auto w-full rounded-lg">
          <div className="flex flex-col mx-[24px] my-auto p-8">
            {teamMembers.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="w-[80px] h-[80px] bg-[#EFF8FA]/30 rounded-full flex items-center justify-center mb-4 relative">
                  <AddTeamIcon className="w-[40px] h-[40px] text-primary-300 m-auto" />
                </div>
                
                <h2 className="text-xl font-bold text-primary-200 mb-2 font-raleway">No team members added</h2>
                
                <p className="text-[#333333]/80 text-base text-center mb-6 max-w-md font-open-sans">
                  Add your team so they can help manage bookings, payments, and settings
                </p>
                
                <button 
                  onClick={() => setShowInviteModal(true)}
                  className="bg-primary-200 text-white px-6 py-3 rounded-md font-raleway font-medium flex items-center"
                >
                  <AddCircle className="w-4 h-4 mr-2" />
                  Add Team Member
                </button>
              </div>
            ) : (
              <div className="space-y-6">
                {teamMembers.map((member: TeamMemberSchema) => (
                  <div key={member.id || member.email} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center">
                      {
                        (member.avatar && !imageError)? (
                          <img
                              src={member.avatar}
                              alt="User Avatar"
                              className="w-[56px] h-[56px] rounded-full object-fit object-center mr-6"
                              onError={() => setImageError(true)}
                          />
                        ) : (
                          <UserAvatar
                              firstName={member.first_name}
                              lastName={member.last_name}
                              username={member.username}
                              email={member.email}
                              size="lg"
                              className="w-[56px] h-[56px] mr-6"
                          />
                        )
                      }
                        <div>
                          <div className="flex flex-row font-medium text-black">{member.first_name} {member.last_name}
                            <div className={`px-2 py-1 font-open-sans text-[10.5px] font-medium rounded-md ${getStatusColorClasses(member.status)}`}> 
                              {
                                  member.status === "invited" ? 
                                    `Invited ${member.created ? moment.utc(member.created).tz(community?.timezone || 'America/New_York').fromNow() : ""}` 
                                      : member.status === "accepted" ? "Active" : member.status === "rejected" ? "Rejected" 
                                      : member.status === "expired" ? 
                                        "Expired" : member.status === "removed" ? 
                                          "Removed" : "N/A"
                              } 
                            </div>
                          </div>
                          <div className="font-normal text-[14px] text-[#333333]/70 mb-1">{member.email}</div>
                          <span className="bg-[#F5F5F5] text-[#4D4D4D]/70 px-2 py-1 text-xs border border-[#E9E9E9]/90 font-raleway text-[16px] font-medium rounded-md">
                            {capitalizeString(member.role)}
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <button 
                          onClick={() => toggleCategory(member?.id || member.email, 'community')}
                          className="text-primary-200 border border-[#E9E9E9]/90 rounded-md px-3 py-2 text-sm font-medium flex items-center"
                        >
                          View Access
                          <svg className={`w-4 h-4 ml-1 transition-transform ${member.permissions.community.expanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        {canDeleteMember(member) && (
                        <button
                          data-testid={`delete-team-member-${member.email}`}
                          onClick={()=>setIsDeletingTeamMember({isOpen: true, member: member})}
                          className="text-gray-400 hover:text-red-600 border border-[#E9E9E9]/90 rounded-md p-2 text-sm font-medium flex items-center"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                        )}
                      </div>
                    </div>

                    {member.permissions.community.expanded && (
                      <TeamPermissionsForm member={member} category="community" categoryData={member.permissions.community} togglePermission={togglePermission} />
                    )}
                  </div>
                ))}
                
                <button 
                  onClick={() => setShowInviteModal(true)}
                  className="w-full bg-[#F8F8F8] rounded-lg py-4 text-black hover:text-gray-700 text-sm font-open-sans font-medium transition-colors"
                >
                  <AddCircle className="w-4 h-4 mr-1 text-primary-300" /> Invite team member
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <DeleteConfirmationModal 
        dataName="team member" 
        isOpen={isDeletingTeamMember.isOpen} 
        data={{...isDeletingTeamMember.member, id: isDeletingTeamMember.member.email, item_name: `${isDeletingTeamMember.member.first_name || ""} ${isDeletingTeamMember.member.last_name || ""}`.trim() || isDeletingTeamMember.member.email}} 
        handleDelete={handleDeleteTeamMember} 
        closeDeleteModal={()=>setIsDeletingTeamMember({isOpen: false, member: {} as TeamMemberSchema})} 
      />

      {showInviteModal && (
        <InviteTeamMemberModal isOpen={showInviteModal} setIsOpen={setShowInviteModal} closeAppModal={()=>setShowInviteModal(false)} setShowInviteModal={setShowInviteModal} showSuccessModal={showSuccessModal} setShowSuccessModal={setShowSuccessModal} />
      )}
    </>
  )
}



type TeamPermissionsFormProps = {
  member: TeamMemberSchema
  category: string
  categoryData: PermissionCategory
  togglePermission: (memberEmail: string, category: string, permission: string) => void
}

export const TeamPermissionsForm: React.FC<TeamPermissionsFormProps> = ({member, category, categoryData, togglePermission}) => {
  const permissions = (categoryData.permissions || {}) as CommunityPermissions;
  return (
    <div className="px-4 pb-3 space-y-2">
    {category === 'community' && (
      <>
        {TEAM_PERMISSION_LABELS.map((option) => (
          <div key={option.id} className="flex items-center justify-between py-2">
            <span className="text-sm text-gray-600">{option.label}</span>
            <input
              type="checkbox"
              checked={Boolean(permissions[option.id])}
              onChange={() => togglePermission(member?.email || "", category, option.id)}
              className="w-4 h-4 text-primary-300 rounded"
              data-testid={`team-permission-${option.id}`}
            />
          </div>
        ))}
      </>
    )}
  </div>
  )
}



export default TeamRoles;
